import {combineReducers} from 'redux';

import * as actions from './installments-report-action';
import type {InstallmentsListRow, InstallmentsReportApiResponse} from './installments-report-api';
import {
    DEFAULT_INSTALLMENTS_LIST_SORT,
    INSTALLMENTS_LIST_PAGE_SIZE,
} from './installments-report-constants';

type SummaryState = {
    hasError: boolean;
    isLoading: boolean;
    data: InstallmentsReportApiResponse | null;
    errorMessage: string | undefined;
};

const defaultSummaryState: SummaryState = {
    data: null,
    hasError: false,
    isLoading: true,
    errorMessage: undefined,
};

function errorMessageFromPayload(payload: unknown): string {
    if (payload && typeof payload === 'object' && 'message' in payload) {
        const msg = (payload as {message?: unknown}).message;
        if (typeof msg === 'string' && msg.length > 0) {
            return msg;
        }
    }
    return 'Failed to load installments report.';
}

export const summaryReducer = (
    state: SummaryState | undefined,
    action: actions.InstallmentsReportAction
): SummaryState => {
    const s = state ?? defaultSummaryState;
    switch (action.type) {
        case actions.ActionTypes.INSTALLMENTS_REPORT_FETCH_REQUEST:
            return {
                data: s.data,
                isLoading: true,
                hasError: false,
                errorMessage: undefined,
            };
        case actions.ActionTypes.INSTALLMENTS_REPORT_FETCH_SUCCESS:
            return {
                data: action.payload,
                isLoading: false,
                hasError: false,
                errorMessage: undefined,
            };
        case actions.ActionTypes.INSTALLMENTS_REPORT_FETCH_FAILURE:
            return {
                data: s.data,
                isLoading: false,
                hasError: true,
                errorMessage: errorMessageFromPayload(action.payload),
            };
        default: {
            return s;
        }
    }
};

type ListState = {
    hasError: boolean;
    isLoading: boolean;
    isFirstLoad: boolean;
    rows: InstallmentsListRow[];
    totalLeads: number;
    page: number;
    limit: number;
    sort: string;
    errorMessage: string | undefined;
};

const defaultListState: ListState = {
    rows: [],
    totalLeads: 0,
    page: 1,
    limit: INSTALLMENTS_LIST_PAGE_SIZE,
    sort: DEFAULT_INSTALLMENTS_LIST_SORT,
    hasError: false,
    isLoading: true,
    isFirstLoad: true,
    errorMessage: undefined,
};

export const listReducer = (
    state: ListState | undefined,
    action: actions.InstallmentsReportAction
): ListState => {
    const s = state ?? defaultListState;
    switch (action.type) {
        case actions.ActionTypes.INSTALLMENTS_LIST_FETCH_REQUEST:
            return {
                ...s,
                isLoading: true,
                hasError: false,
                errorMessage: undefined,
            };
        case actions.ActionTypes.INSTALLMENTS_LIST_FETCH_SUCCESS:
            return {
                rows: action.payload.rows,
                totalLeads: action.payload.totalLeads,
                page: action.payload.page,
                limit: action.payload.limit,
                sort: action.payload.sort,
                isLoading: false,
                isFirstLoad: false,
                hasError: false,
                errorMessage: undefined,
            };
        case actions.ActionTypes.INSTALLMENTS_LIST_FETCH_FAILURE:
            return {
                ...s,
                isLoading: false,
                isFirstLoad: false,
                hasError: true,
                errorMessage: errorMessageFromPayload(action.payload),
            };
        default: {
            return s;
        }
    }
};

export const installmentsReportReducer = combineReducers({
    summary: summaryReducer,
    list: listReducer,
});

export type InstallmentsReportState = {
    summary: SummaryState;
    list: ListState;
};
